import React, { useState, useEffect } from 'react';
import { Clock, FileText, Search, Activity, ExternalLink } from 'lucide-react';

interface HistoryEntry {
  id: string;
  entry_type: 'search' | 'report' | 'change_detection';
  title: string;
  description?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

interface GEOINTHistoryProps {
  onLocationSelect?: (coordinates: [number, number], name: string) => void;
}

const GEOINTHistory: React.FC<GEOINTHistoryProps> = ({ onLocationSelect }) => {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string | null>(null);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');
      let url = '/api/geoint/wayback/history?limit=50';
      if (filter) url += `&entry_type=${filter}`;

      const response = await fetch(url, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [filter]);

  const getIcon = (type: string) => {
    switch (type) {
      case 'search': return <Search className="w-4 h-4 text-blue-400" />;
      case 'report': return <FileText className="w-4 h-4 text-green-400" />;
      case 'change_detection': return <Activity className="w-4 h-4 text-orange-400" />;
      default: return <Clock className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      search: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      report: 'bg-green-500/20 text-green-400 border-green-500/30',
      change_detection: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    };
    const labels: Record<string, string> = {
      search: 'Search',
      report: 'Report',
      change_detection: 'Detection',
    };
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${colors[type] || 'bg-muted text-muted-foreground'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const formatDate = (iso: string) => {
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      const diffHrs = Math.floor(diffMins / 60);
      if (diffHrs < 24) return `${diffHrs}h ago`;
      const diffDays = Math.floor(diffHrs / 24);
      if (diffDays < 7) return `${diffDays}d ago`;
      return d.toLocaleDateString();
    } catch {
      return iso;
    }
  };

  const handleEntryClick = (entry: HistoryEntry) => {
    if (entry.entry_type === 'report' && entry.metadata?.pdf_url) {
      window.open(entry.metadata.pdf_url, '_blank');
    } else if (entry.metadata?.coordinates && onLocationSelect) {
      onLocationSelect(entry.metadata.coordinates, entry.metadata.location || '');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Clock className="w-5 h-5" />
          History
        </h3>
        <button
          onClick={fetchHistory}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Filter buttons */}
      <div className="flex gap-1.5">
        {[
          { key: null, label: 'All' },
          { key: 'search', label: 'Searches' },
          { key: 'report', label: 'Reports' },
          { key: 'change_detection', label: 'Detections' },
        ].map((f) => (
          <button
            key={f.key || 'all'}
            onClick={() => setFilter(f.key)}
            className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
              filter === f.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Entries */}
      <div className="space-y-1.5">
        {loading && (
          <div className="text-sm text-muted-foreground text-center py-4">Loading...</div>
        )}

        {!loading && entries.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-8">
            No history yet. Search locations, run change detection, or generate reports to build your history.
          </div>
        )}

        {entries.map((entry) => (
          <button
            key={entry.id}
            onClick={() => handleEntryClick(entry)}
            className="w-full text-left p-2.5 bg-muted/50 hover:bg-muted rounded-md transition-colors group"
          >
            <div className="flex items-start gap-2">
              <div className="mt-0.5">{getIcon(entry.entry_type)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-foreground truncate">
                    {entry.title}
                  </span>
                  {getTypeBadge(entry.entry_type)}
                </div>
                {entry.description && (
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {entry.description}
                  </p>
                )}
                <span className="text-[10px] text-muted-foreground/70 mt-0.5 block">
                  {formatDate(entry.created_at)}
                </span>
              </div>
              {entry.entry_type === 'report' && entry.metadata?.pdf_url && (
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default GEOINTHistory;
